import React from 'react';
import { connect, DispatchProp } from 'react-redux';
import { TaskManager } from '../../helpers/form.new/task.manager';
import { IGlobalState } from '../../store';
import { ILocation } from '../../store/reducers/locations';
import { PAGE_CONTEXT } from '../../constants';
import { getRegionId } from '../../helpers';
import { getSelectionAbExperimentAnalytics } from '../../helpers/experiments';
import { clearFormData, extendFbPayload, extendFormStore, setPageContext } from '../../store/reducers/formStore';
import { MainSkeleton } from '../landing.common/main.skeleton';
import { Experiments } from '../../store/reducers/abData';
import { LandingContent } from './components/landing.content';
import { CardsContext } from './context';
import { CARD_PURPOSES } from './components/landing.content/components/top.form';

interface OwnProps {
    userId?: string;
    isMobile: boolean;
    isTablet: boolean;
    formData: FE.FormData;
    autoRun?: boolean;
    cookies?: Record<string, string>;
    guid?: string;
    isLoadingData?: boolean;
}

interface StateProps {
    page: string;
    region: ILocation;
    experiments: Experiments;
    areExperimentsDefault?: boolean;
    showSkeleton?: boolean;
}

type Props = DispatchProp & OwnProps & StateProps;

type State = Omit<React.ContextType<typeof CardsContext>, 'taskManager' | 'setShowLanding'> & {
    mounted: boolean;
};

export class LandingCreditCardComponent extends React.Component<Props, State> {
    private taskManager = new TaskManager();

    state: State = {
        mounted: false,
        showLanding: true,
        topFormAdditionalData: {
            purpose: CARD_PURPOSES[0].value,
        },
        setTopFormAdditionalData: (data) =>
            this.setState((previous) => ({ topFormAdditionalData: { ...previous.topFormAdditionalData, ...data } })),
    };

    async componentDidMount() {
        await this.setFbPayload({ onMount: true });
        const { formData, dispatch } = this.props;

        dispatch(clearFormData(formData));
        dispatch(setPageContext(PAGE_CONTEXT.DEFAULT));

        this.setState({ mounted: true });
    }

    componentDidUpdate(prevProps: Readonly<Props>) {
        if (
            prevProps.userId !== this.props.userId ||
            prevProps.region.id !== this.props.region.id ||
            prevProps.isMobile !== this.props.isMobile ||
            prevProps.isTablet !== this.props.isTablet
        ) {
            setTimeout(this.setFbPayload, 0);
        }
    }

    setFbPayload = async ({ onMount = false } = {}) => {
        const {
            isTablet,
            isMobile,
            page,
            userId,
            region,
            dispatch,
            autoRun,
            experiments,
            areExperimentsDefault,
            cookies: { _ga: googleAnalyticId, _ym_uid: yandexMetricsId } = {},
            guid,
        } = this.props;
        const device_type = isMobile ? 'Мобильный' : isTablet ? 'Планшет' : 'Десктоп';

        const fbPayload = {
            regionId: getRegionId(region),
            regionName: region.name,
            region_latitude: region.latitude,
            region_longitude: region.longitude,
            region_full_name: region.fullName,
            ...(onMount
                ? {
                      device_type,
                      sourceUrl: typeof window !== 'undefined' ? window.location.href : undefined,
                      abc_group_creditselection: getSelectionAbExperimentAnalytics(experiments),
                      ...(areExperimentsDefault ? { areExperimentsDefault } : {}),
                  }
                : {}),
        };
        dispatch(extendFbPayload(fbPayload));

        dispatch(
            extendFormStore({
                region,
                userId,
                ...(onMount
                    ? {
                          page,
                          autoRun,
                          googleAnalyticId,
                          yandexMetricsId,
                          guid,
                      }
                    : {}),
            }),
        );
    };

    setShowLanding = async (value: boolean) => {
        if (!value) {
            await Promise.all(this.taskManager.tasks);
            this.props.dispatch(extendFormStore({ holdOn: false }));
            this.setState({ showLanding: false });
        } else {
            this.setState({ showLanding: value });
        }
    };

    render() {
        const { showLanding, mounted } = this.state;
        const { isLoadingData } = this.props;
        const { taskManager } = this;

        const context = {
            ...this.state,
            taskManager,
        };

        const isShowForm = !isLoadingData && mounted;

        return (
            <CardsContext.Provider value={context}>
                {(!isShowForm || this.props.showSkeleton) && <MainSkeleton />}
                {isShowForm && <LandingContent showLanding={showLanding} setShowLanding={this.setShowLanding} />}
            </CardsContext.Provider>
        );
    }
}

export const LandingCreditCard = connect((state: IGlobalState) => ({
    region: state.locations.currentLocation,
    page: state.selection.page,
    areExperimentsDefault: state.abData.isDefaultVariants,
    experiments: state.abData.experiments,
    showSkeleton: Boolean(state.formStore.autoRun || state.formStore.holdOn),
}))(LandingCreditCardComponent);
